/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React from 'react';
import {
  string,
  arrayOf,
  func,
  shape,
  bool,
  oneOf,
} from 'prop-types';
import { addEventListener } from 'consolidated-events';
import styleVariables from 'styles/variables';
import classNames from 'classnames';
import { startAnimation } from './helpers/animation';
import Icon from './Icon';
import DialogBox from './DialogBox';
import Option from './Option';
import OutsideClickHandler from './OutsideClickHandler';
import './InlineInput.scss';

class InlineInput extends React.Component {
  constructor(props) {
    super(props);

    const { showCustomPanel } = this.props;

    this.state = {
      isCustomPanelShow: showCustomPanel,
      isFocused: false,
    };

    this.widthRef = React.createRef();
    this.inputRef = React.createRef();
    this.arrowRef = React.createRef();
    this.customPanelRef = React.createRef();
    this.suggestionPanelRef = React.createRef();
    this.localOnClick = this.localOnClick.bind(this);
    this.hideCustomPanel = this.hideCustomPanel.bind(this);
    this.localOnFocus = this.localOnFocus.bind(this);
    this.localOnBlur = this.localOnBlur.bind(this);
    this.updateWidth = this.updateWidth.bind(this);
    this.localOnKeyPress = this.localOnKeyPress.bind(this);
  }

  componentDidMount() {
    this.removeResizeListeners = addEventListener(window, 'resize', this.updateWidth);

    startAnimation(() => {
      this.updateWidth();
    });
  }

  componentDidUpdate() {
    this.updateWidth();
  }

  componentWillUnmount() {
    if (this.removeResizeListeners) this.removeResizeListeners();
  }

  focus = () => {
    this.inputRef?.current.focus();
  }

  updateWidth() {
    const { current: widthEl } = this.widthRef;
    const { current: inputEl } = this.inputRef;
    const { current: arrowEl } = this.arrowRef;
    const { current: customPanelEl } = this.customPanelRef;
    const { current: suggestionPanelEl } = this.suggestionPanelRef;
    if (!widthEl || !inputEl || !window) return;

    const { x } = inputEl.getBoundingClientRect();
    // Hack to make input width totally depends on input text / placeholder
    if (typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Edge') > -1) {
      inputEl.style.width = `${window.innerWidth < widthEl.clientWidth ? window.innerWidth - 70 + 3 : widthEl.clientWidth + 3}px`;
    } else {
      inputEl.style.width = `${window.innerWidth < widthEl.clientWidth ? window.innerWidth - 70 : widthEl.clientWidth}px`;
    }
    if (window.innerWidth < styleVariables.screenXsMax) {
      // mobile

      // Hack to set mobile custom panel
      if (customPanelEl) {
        customPanelEl.style.left = `${-1 * x}px`;
        customPanelEl.style.width = `${window.innerWidth}px`;
      }

      // Hack to set mobile suggestion panel
      if (suggestionPanelEl) {
        suggestionPanelEl.style.left = `${-1 * x}px`;
        suggestionPanelEl.style.width = `${window.innerWidth}px`;
      }

      // Hack to make arrow always show in the middle of input
      if (arrowEl) {
        arrowEl.style.paddingLeft = `${x + (widthEl.clientWidth / 2)}px`;
      }
    } else {
      if (customPanelEl) {
        customPanelEl.style.left = null;
        customPanelEl.style.width = null;
      }

      if (suggestionPanelEl) {
        suggestionPanelEl.style.left = null;
        suggestionPanelEl.style.width = null;
      }

      // Hack to make arrow always show in the middle of input
      if (arrowEl) {
        arrowEl.style.paddingLeft = `${widthEl.clientWidth / 2}px`;
      }
    }
  }

  hideCustomPanel() {
    this.setState({ isCustomPanelShow: false });
  }

  localOnFocus() {
    const { onFocus } = this.props;
    if (onFocus) onFocus();
    this.setState({ isFocused: true });
  }

  localOnBlur() {
    const { onBlur } = this.props;
    if (onBlur) onBlur();
    this.setState({ isFocused: false });
  }

  localOnClick() {
    const { customPanel } = this.props;
    if (customPanel) {
      this.setState({ isCustomPanelShow: true });
    }
  }

  localOnKeyPress(e) {
    const {
      value,
      onChange,
      suggestions,
    } = this.props;
    const matchedSuggestions = suggestions
      .filter((
        {
          label: suggestionLabel,
          value: suggestionValue,
        }
      ) => (suggestionLabel || suggestionValue)
        .toLowerCase().includes(value.toLowerCase()));

    if (e.key === 'Enter') {
      if ((value.length > 0) && (matchedSuggestions.length > 0) && onChange) {
        onChange({
          label: matchedSuggestions[0].label,
          value: matchedSuggestions[0].value,
          isValueFromSuggestion: true,
        });
        this.localOnBlur();
      }
    }
  }

  render() {
    const {
      isCustomPanelShow,
      isFocused,
    } = this.state;
    const {
      type,
      value,
      onChange,
      placeholder,
      customPanel,
      suggestions,
      isValueFromSuggestion,
      isShowRemoveButton,
      onCustomPanelOutsideClick,
      overrideCustomPanelOutsideClick,
      onRemoveButtonClick,
      large,
      isCustomPanelPadded,
      customPanelClassName,
      dayPicker,
      dataSelectorId,
      disabled,
      dataSelectorValue,
    } = this.props;

    let readOnly = false;
    switch (type) {
      case 'custom-panel':
        readOnly = true;
        break;
      case 'suggestion-only':
        readOnly = isValueFromSuggestion;
        break;
      default:
        break;
    }

    // Filter suggestions by checking if suggestions contains current value
    const matchedSuggestions = suggestions
      .filter(({
        label: suggestionLabel,
        value: suggestionValue,
      }) => (suggestionLabel || suggestionValue)
        .toLowerCase().includes(value.toLowerCase()));

    return (
      <span className="inline-input-outer-wrapper" data-selector-type="inline-input">
        <span
          className={classNames({
            'inline-input': true,
            [type]: true,
            large,
          })}
          onClick={disabled ? undefined : this.localOnClick}
        >
          <input
            readOnly={readOnly}
            ref={this.inputRef}
            value={value}
            placeholder={placeholder}
            onKeyPress={this.localOnKeyPress}
            onChange={(e) => {
              if (onChange) {
                onChange({
                  label: e.target.value,
                  value: e.target.value,
                  isValueFromSuggestion: false,
                });
              }
            }}
            onFocus={this.localOnFocus}
            onBlur={this.localOnBlur}
            data-selector={dataSelectorId}
            data-selector-value={dataSelectorValue}
            data-selector-type="inline-input-input"
          />
          {isShowRemoveButton && (
            <span
              className="remove-button"
              onClick={(e) => {
                // Prevent triggering input onclick event
                e.stopPropagation();
                if (onRemoveButtonClick) onRemoveButtonClick();
              }}
              data-selector="remove"
            >
              <Icon secondary name="cancel" />
            </span>
          )}
          <span ref={this.widthRef} className="width-ref">
            {value || placeholder}
          </span>
        </span>
        {customPanel && isCustomPanelShow && (
          <DialogBox
            className={classNames({
              'custom-panel-wrapper': true,
              [customPanelClassName]: true,
            })}
            dayPicker={dayPicker}
            isShowArrow
            innerRef={this.customPanelRef}
            arrowRef={this.arrowRef}
          >
            <OutsideClickHandler
              display="block"
              onOutsideClick={() => {
                if (onCustomPanelOutsideClick) onCustomPanelOutsideClick();
                if (overrideCustomPanelOutsideClick) return;
                this.setState({ isCustomPanelShow: false });
              }}
            >
              <div
                className={classNames({
                  'custom-panel-padding': isCustomPanelPadded,
                })}
              >
                {customPanel({ hideCustomPanel: this.hideCustomPanel })}
              </div>
            </OutsideClickHandler>
          </DialogBox>
        )}
        {isFocused
        && !isValueFromSuggestion
        && matchedSuggestions
        && matchedSuggestions.length > 0
        && value.length > 0
        && (
          <DialogBox
            className="suggestions-wrapper"
            isShowArrow
            innerRef={this.suggestionPanelRef}
            arrowRef={this.arrowRef}
          >
            {matchedSuggestions.map(({ label: suggestionLabel, value: suggestionValue }) => (
              <Option
                className="suggestion"
                key={suggestionValue}
                onMouseDown={() => {
                  if (onChange) {
                    onChange({
                      label: suggestionLabel,
                      value: suggestionValue,
                      isValueFromSuggestion: true,
                    });

                    // Fix Edge browser
                    this.inputRef.current.blur();
                  }
                }}
              >
                {suggestionLabel || suggestionValue}
              </Option>
            ))}
          </DialogBox>
        )}
      </span>
    );
  }
}

InlineInput.propTypes = {
  value: string,
  isValueFromSuggestion: bool,
  placeholder: string,
  onChange: func,
  onFocus: func,
  onBlur: func,
  type: oneOf(['suggestion-only', 'custom-panel']),
  customPanel: func,
  showCustomPanel: bool,
  suggestions: arrayOf(shape({})),
  isShowRemoveButton: bool,
  onCustomPanelOutsideClick: func,
  overrideCustomPanelOutsideClick: bool,
  onRemoveButtonClick: func,
  large: bool,
  isCustomPanelPadded: bool,
  customPanelClassName: string,
  dayPicker: bool,
  dataSelectorId: string,
  disabled: bool,
  dataSelectorValue: string,
};

InlineInput.defaultProps = {
  value: '',
  isValueFromSuggestion: false,
  placeholder: null,
  onChange: undefined,
  onBlur: undefined,
  onFocus: undefined,
  type: null,
  customPanel: undefined,
  showCustomPanel: false,
  suggestions: [],
  isShowRemoveButton: false,
  onCustomPanelOutsideClick: undefined,
  overrideCustomPanelOutsideClick: false,
  onRemoveButtonClick: undefined,
  large: false,
  isCustomPanelPadded: true,
  customPanelClassName: null,
  dayPicker: null,
  dataSelectorId: null,
  disabled: false,
  dataSelectorValue: null,
};

export default InlineInput;
